import type { components } from '@/api/schema';
import { Roles } from '@/auth/roles';
import { asArray, asRecord, parseJson, prettyJson } from '@/lib/json';
import type { MessageTemplateDto, TenantSettingsDto } from './api';
import { recipientLines, type SettingsForm } from './schemas';

type TemplateStatus = components['schemas']['TemplateStatus'];

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

export function healthVariant(h: string | undefined): 'success' | 'destructive' | 'default' {
  return h === 'healthy' ? 'success' : h === 'unhealthy' ? 'destructive' : 'default';
}

/** Template packs shipped per agent template (schema/examples/templates). */
export const TEMPLATE_PACKS = ['document-collector', 'timesheet-chaser'] as const;

export interface TemplateRow {
  name: string;
  pack: string | null;
  category: string;
  byLanguage: Record<string, MessageTemplateDto | undefined>;
}

/** Groups templates by name with one cell per language (08 §2.3: approval status per language). */
export function groupTemplates(items: readonly MessageTemplateDto[]): TemplateRow[] {
  const rows = new Map<string, TemplateRow>();
  for (const tpl of items) {
    const row = rows.get(tpl.name) ?? { name: tpl.name, pack: tpl.pack, category: tpl.category, byLanguage: {} };
    row.byLanguage[tpl.language] = tpl;
    row.pack ??= tpl.pack;
    rows.set(tpl.name, row);
  }
  return [...rows.values()].sort((a, b) => (a.pack ?? '').localeCompare(b.pack ?? '') || a.name.localeCompare(b.name));
}

export function statusVariant(s: TemplateStatus | undefined): 'success' | 'warning' | 'destructive' | 'default' {
  switch (s) {
    case 'approved':
      return 'success';
    case 'pending':
    case 'draft':
      return 'warning';
    case 'rejected':
    case 'disabled':
    case 'invalid':
    case 'paused':
      return 'destructive';
    default:
      return 'default';
  }
}

export const TENANT_ROLES = [Roles.TenantAdmin, Roles.TenantBuilder, Roles.TenantReviewer, Roles.TenantViewer] as const;

/** Maps the stored JSON settings (quietHours/notifications/reviewers) onto the flat form. */
export function settingsToForm(s: TenantSettingsDto): SettingsForm {
  const q = asRecord(s.quietHours);
  const n = asRecord(s.notifications);
  return {
    quietStart: str(q['start'], '20:00'),
    quietEnd: str(q['end'], '07:00'),
    quietTimezone: str(q['timezone'], 'Europe/Zurich'),
    weekendsSilent: q['weekendsSilent'] === true,
    notificationEmails: asArray(n['emails']).filter((e): e is string => typeof e === 'string').join(', '),
    webhookUrl: str(n['webhookUrl']),
    digest: n['digest'] === true,
    reviewerUserIds: asArray(s.reviewers).filter((e): e is string => typeof e === 'string'),
    fallbackText: s.fallbackText ?? '',
    i18nOverrides: prettyJson(asRecord(s.i18nOverrides)),
    testRecipients: (s.testRecipients ?? []).join('\n'),
  };
}

export function formToSettings(v: SettingsForm, current: TenantSettingsDto): TenantSettingsDto {
  const overrides = parseJson(v.i18nOverrides);
  return {
    ...current,
    quietHours: { start: v.quietStart, end: v.quietEnd, timezone: v.quietTimezone, weekendsSilent: v.weekendsSilent } as never,
    notifications: {
      ...asRecord(current.notifications),
      emails: v.notificationEmails.split(/[,\s]+/).filter(Boolean),
      webhookUrl: v.webhookUrl || null,
      digest: v.digest,
    } as never,
    reviewers: v.reviewerUserIds as never,
    i18nOverrides: (overrides.ok && overrides.value !== null ? overrides.value : {}) as never,
    fallbackText: v.fallbackText || null,
    testRecipients: recipientLines(v.testRecipients),
  };
}

/** Twilio credentials for the write-only secret, or null to keep the stored ones (05 §3). */
export function smsCredentials(v: { accountSid: string; authToken: string }): string | null {
  const sid = v.accountSid.trim();
  const token = v.authToken.trim();
  return sid && token ? JSON.stringify({ accountSid: sid, authToken: token }) : null;
}

/** SendGrid API key or SES access-key JSON for the write-only secret, or null to keep the stored ones (05 §4). */
export function emailCredentials(v: {
  provider: 'sendgrid' | 'ses';
  apiKey: string;
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
}): string | null {
  if (v.provider === 'sendgrid') return v.apiKey.trim() || null;
  const id = v.accessKeyId.trim();
  const secret = v.secretAccessKey.trim();
  const region = v.region.trim();
  return id && secret && region ? JSON.stringify({ accessKeyId: id, secretAccessKey: secret, region }) : null;
}

export interface BrandingFields {
  logoUrl: string;
  primaryColor: string;
  footerText: string;
  imprint: string;
}

/** Email branding for the channel config: empty fields omitted; null when nothing is set. */
export function brandingFromForm(v: BrandingFields): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const [k, raw] of Object.entries(v)) {
    const value = raw.trim();
    if (value) out[k] = value;
  }
  return Object.keys(out).length ? out : null;
}

export function brandingToForm(branding: unknown): BrandingFields {
  const b = asRecord(branding);
  return { logoUrl: str(b['logoUrl']), primaryColor: str(b['primaryColor']), footerText: str(b['footerText']), imprint: str(b['imprint']) };
}
